// src/components/store/sections/hero-banner/HeroBrushSplit.tsx
"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

export interface HeroBannerItem {
  id: number;
  badge_title?: string;
  title: string;
  subtitle?: string;
  button_text?: string;
  target_url?: string;
  image?: string | null;
  bg_color?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
}

interface HeroBrushSplitProps {
  banners?: HeroBannerItem[];
  primaryColor?: string;
  secondaryColor?: string;
  subdomain?: string;
}

// 🌟 হুবহু রেফারেন্স ইমেজের ৩টি ব্যানার স্লাইড (Orange, Red, Blue)
const DEFAULT_SLIDES = [
  {
    id: 1,
    company_title: "COMPANY",
    company_sub: "TEXT HERE",
    subtitle: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse in finibus purus.",
    big_title_1: "MODERN",
    big_title_2: "BANNER",
    tagline: "YOUR TEXT HERE",
    button_text: "Shop Now",
    target_url: "/products",
    gradient_from: "#f59e0b",
    gradient_to: "#ea580c",
  },
  {
    id: 2,
    company_title: "COMPANY",
    company_sub: "TEXT HERE",
    subtitle: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse in finibus purus.",
    big_title_1: "MODERN",
    big_title_2: "BANNER",
    tagline: "YOUR TEXT HERE",
    button_text: "Discover Deals",
    target_url: "/products?deals=true",
    gradient_from: "#dc2626",
    gradient_to: "#991b1b",
  },
  {
    id: 3,
    company_title: "COMPANY",
    company_sub: "TEXT HERE",
    subtitle: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse in finibus purus.",
    big_title_1: "MODERN",
    big_title_2: "BANNER",
    tagline: "YOUR TEXT HERE",
    button_text: "View Collection",
    target_url: "/categories",
    gradient_from: "#0284c7",
    gradient_to: "#2563eb",
  },
];

export default function HeroBrushSplit({
  banners = [],
  primaryColor = "#ea580c",
  secondaryColor = "#f59e0b",
  subdomain = "nextlook",
}: HeroBrushSplitProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const displayBanners = banners && banners.length > 0 ? banners : DEFAULT_SLIDES;

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === displayBanners.length - 1 ? 0 : prev + 1));
  }, [displayBanners.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? displayBanners.length - 1 : prev - 1));
  }, [displayBanners.length]);

  useEffect(() => {
    if (displayBanners.length <= 1) return;
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [displayBanners.length, nextSlide]);

  const activeSlide: any = displayBanners[currentIndex] || displayBanners[0];

  const currentGradientFrom = activeSlide.gradient_color_1 || activeSlide.gradient_from || primaryColor;
  const currentGradientTo = activeSlide.gradient_color_2 || activeSlide.gradient_to || secondaryColor;

  const imageUrl = activeSlide?.image
    ? activeSlide.image.startsWith("http")
      ? activeSlide.image
      : `http://127.0.0.1:8000${activeSlide.image.startsWith("/") ? "" : "/"}${activeSlide.image}`
    : null;

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 pt-4">
      <div className="relative w-full rounded-3xl overflow-hidden shadow-xl border border-slate-200/90 bg-white min-h-[340px] sm:min-h-[400px] md:min-h-[440px] flex flex-col justify-between select-none">
        
        {/* 🌟 ব্যাকগ্রাউন্ড স্প্লিট লেআউট */}
        <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-12 pointer-events-none">
          {/* বাম পাশ: পিওর হোয়াইট */}
          <div className="md:col-span-5 bg-white" />

          {/* ডান পাশ: গ্রেডিয়েন্ট কালার + খাঁটি পেইন্ট ব্রাশ এজ */}
          <div 
            className="md:col-span-7 relative transition-all duration-700"
            style={{
              background: `linear-gradient(135deg, ${currentGradientFrom}, ${currentGradientTo})`,
            }}
          >
            {/* 🌟 খাঁটি গ্রাঞ্জ পেইন্ট ব্রাশ স্ট্রোক ডিভাইডার (বামদিকে ছড়িয়ে পড়া ব্রাশ টেক্সচার) */}
            <svg
              className="absolute -left-[65px] sm:-left-[95px] md:-left-[115px] top-0 bottom-0 h-full w-[70px] sm:w-[100px] md:w-[120px] pointer-events-none z-10 hidden md:block"
              viewBox="0 0 120 500"
              preserveAspectRatio="none"
              style={{ fill: currentGradientFrom }}
            >
              {/* মেইন ব্রাশ টিথ ও আঁকাবাঁকা রগড এজ */}
              <path d="M120,0 L120,500 L80,500 
                C70,490 60,480 40,475 
                C65,465 75,455 60,445 
                C35,435 15,430 3,420 
                C25,410 50,405 45,395 
                C25,385 10,380 0,365 
                C20,355 45,350 40,335 
                C20,325 5,315 15,300 
                C35,290 60,285 50,270 
                C30,255 10,250 1,235 
                C22,225 48,220 40,205 
                C20,195 8,185 18,170 
                C38,160 62,155 52,140 
                C32,125 15,120 5,105 
                C25,95 50,90 42,75 
                C22,65 10,55 20,40 
                C40,30 65,25 55,10 
                C70,5 78,2 80,0 Z" 
              />
              {/* রঙের ব্রাশের সূক্ষ্ম আঁশ (Bristle streaks) */}
              <rect x="0" y="363" width="22" height="3.5" rx="1.5" />
              <rect x="1" y="233" width="20" height="3" rx="1.5" />
              <rect x="3" y="418" width="18" height="3" rx="1.5" />
              <rect x="4" y="103" width="24" height="3" rx="1.5" />
              <rect x="15" y="298" width="16" height="2.5" rx="1.2" />
              <rect x="18" y="168" width="18" height="2.5" rx="1.2" />
              <rect x="12" y="38" width="20" height="3" rx="1.5" />

              {/* পেইন্টের ছিটা ও ড্রপলেটস (Paint Splatter Droplets) */}
              <circle cx="-6" cy="365" r="3" />
              <circle cx="-16" cy="370" r="2" />
              <circle cx="-8" cy="235" r="3.2" />
              <circle cx="-18" cy="240" r="2" />
              <circle cx="-5" cy="420" r="2.5" />
              <circle cx="-14" cy="415" r="1.8" />
              <circle cx="-8" cy="105" r="3" />
              <circle cx="-18" cy="110" r="2" />
              <circle cx="-6" cy="170" r="2.8" />
              <circle cx="-15" cy="165" r="1.6" />
              <circle cx="-8" cy="40" r="3" />
              <circle cx="-16" cy="45" r="1.8" />
              <circle cx="-10" cy="285" r="2.2" />
              <circle cx="-12" cy="140" r="2.5" />
            </svg>
          </div>
        </div>

        {/* 🌟 কনটেন্ট লেআউট (রেফারেন্স ইমেজের হুবহু টেক্সট স্টাইল) */}
        <div className="relative z-20 grid grid-cols-1 md:grid-cols-12 items-center flex-1 p-6 sm:p-10 md:p-14 gap-8">
          
          {/* বাম পাশ: COMPANY TEXT HERE */}
          <div className="md:col-span-5 space-y-3 sm:space-y-4 text-left">
            <div>
              <h2 
                style={{ color: currentGradientFrom }}
                className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-none uppercase"
              >
                {activeSlide.company_title || activeSlide.title?.split(" ")[0] || "COMPANY"}
              </h2>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 tracking-wider uppercase mt-1">
                {activeSlide.company_sub || activeSlide.title?.split(" ").slice(1).join(" ") || "TEXT HERE"}
              </h3>
            </div>

            <p className="text-xs sm:text-sm text-slate-500 font-medium leading-relaxed max-w-sm line-clamp-3">
              {activeSlide.subtitle || "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse in finibus purus."}
            </p>

            <div className="pt-2 sm:pt-4">
              <Link
                href={activeSlide.target_url || "/products"}
                style={{ backgroundColor: currentGradientFrom }}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-xl text-white text-xs font-black shadow-lg hover:opacity-90 active:scale-95 transition-all cursor-pointer"
              >
                <span>{activeSlide.button_text || "Shop Now"}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* ডান পাশ: MODERN BANNER / YOUR TEXT HERE */}
          <div className="md:col-span-7 flex flex-col md:items-end justify-center text-white space-y-2 md:text-right pl-0 md:pl-8">
            {imageUrl ? (
              <div className="relative w-full h-52 sm:h-64 md:h-76 drop-shadow-2xl flex items-center justify-center md:justify-end">
                <Image
                  src={imageUrl}
                  alt="Banner"
                  fill
                  unoptimized
                  className="object-contain object-center md:object-right"
                />
              </div>
            ) : (
              <div className="space-y-2 animate-in fade-in duration-300">
                <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight text-white leading-none drop-shadow-sm">
                  {activeSlide.big_title_1 || "MODERN"}
                  <br />
                  <span className="font-extrabold text-white/95">
                    {activeSlide.big_title_2 || "BANNER"}
                  </span>
                </h1>

                <span className="text-xs sm:text-sm font-black uppercase tracking-[0.25em] text-white/85 block pt-2">
                  {activeSlide.tagline || "YOUR TEXT HERE"}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 🌟 নেভিগেশন কন্ট্রোলস */}
        <div className="relative z-20 px-6 sm:px-10 pb-5 sm:pb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {displayBanners.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentIndex(idx)}
                className={`h-2 rounded-full transition-all cursor-pointer ${
                  idx === currentIndex
                    ? "w-7 bg-slate-900"
                    : "w-2 bg-slate-300 hover:bg-slate-400"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

          {displayBanners.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={prevSlide}
                className="w-8 h-8 rounded-full bg-white/80 hover:bg-white text-slate-800 border border-slate-200/80 shadow-xs flex items-center justify-center transition-all cursor-pointer"
                aria-label="Previous Slide"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={nextSlide}
                className="w-8 h-8 rounded-full bg-white/80 hover:bg-white text-slate-800 border border-slate-200/80 shadow-xs flex items-center justify-center transition-all cursor-pointer"
                aria-label="Next Slide"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}